import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Privacy Policy | Woods Spine & Wellness"
        description="Privacy Policy for Woods Spine & Wellness in Longview, TX."
        canonical="https://woodsspine.com/privacy-policy"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Header Section */}
        <section className="bg-primary py-16 text-primary-foreground text-center">
          <div className="container mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Woods Spine & Wellness Privacy Policy</h1>
            <p className="text-xl opacity-90">Last Updated: 01/01/2026</p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="shadow-lg border-0">
              <CardContent className="p-8 md:p-12 prose prose-slate max-w-none">
                <p className="text-lg text-muted-foreground mb-8">
                  At <strong>Woods Spine & Wellness</strong> (“we,” “us,” or “our”), we are committed to protecting your privacy and safeguarding your personal information. This Privacy Policy explains how we collect, use, disclose, and protect information when you visit our website, communicate with us, or use our services, including SMS messaging, email communications, appointment scheduling, and website analytics.
                </p>
                <p className="text-lg text-muted-foreground mb-8">
                  By using our website or opting into our communications, you agree to this Privacy Policy and our Terms of Use.
                </p>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">1. Information We Collect</h3>
                <h4 className="text-lg font-semibold mb-2">Information You Provide</h4>
                <p className="mb-4">We may collect personal information that you voluntarily provide, including but not limited to:</p>
                <ul className="list-disc pl-6 mb-6 space-y-1">
                  <li>Name</li>
                  <li>Phone number</li>
                  <li>Email address</li>
                  <li>Mailing address</li>
                  <li>Appointment and service details</li>
                  <li>Information submitted through forms, scheduling tools, or direct contact</li>
                </ul>

                <h4 className="text-lg font-semibold mb-2">Communications & Consent Data</h4>
                <p className="mb-4">If you opt in to receive SMS or email communications, we collect and maintain:</p>
                <ul className="list-disc pl-6 mb-6 space-y-1">
                  <li>Communication preferences</li>
                  <li>Records of consent</li>
                  <li>Opt-in and opt-out activity</li>
                </ul>

                <h4 className="text-lg font-semibold mb-2">Automatically Collected Information</h4>
                <p className="mb-4">When you interact with our website, we may automatically collect information using cookies and similar technologies, such as:</p>
                <ul className="list-disc pl-6 mb-6 space-y-1">
                  <li>IP address</li>
                  <li>Device and browser information</li>
                  <li>Pages visited and interactions</li>
                  <li>Session duration and referral data</li>
                </ul>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">2. How We Use Your Information</h3>
                <p className="mb-4">We use the information we collect to:</p>
                <ul className="list-disc pl-6 mb-6 space-y-1">
                  <li>Provide requested services and customer support</li>
                  <li>Schedule appointments and send confirmations or reminders</li>
                  <li>Send SMS and email communications <strong>only when you have explicitly opted in</strong></li>
                  <li>Improve website performance and user experience</li>
                  <li>Maintain compliance records for consent and opt-out requirements</li>
                  <li>Protect against fraud, misuse, and security risks</li>
                </ul>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">3. SMS and Email Communications (A2P Compliance)</h3>
                <p className="mb-4"><strong>SMS and email messages are sent only to individuals who have explicitly opted in.</strong></p>
                <p className="mb-4"><strong>We do not sell, share, or disclose mobile phone numbers, SMS opt-in data, or consent records for marketing or promotional purposes.</strong></p>
                <ul className="list-disc pl-6 mb-6 space-y-1">
                  <li>Message frequency may vary.</li>
                  <li>Message and data rates may apply depending on your carrier.</li>
                </ul>

                <h4 className="text-lg font-semibold mb-2">SMS Opt-Out</h4>
                <p className="mb-4">You may opt out of SMS messages at any time by replying <strong>STOP</strong> to any message. After opting out, you will receive a confirmation and will no longer receive SMS messages unless you opt back in.</p>

                <h4 className="text-lg font-semibold mb-2">Email Opt-Out</h4>
                <p className="mb-4">You may unsubscribe from marketing emails at any time using the unsubscribe link included in the email.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">4. Cookies and Tracking Technologies</h3>
                <p className="mb-4">We use cookies and similar technologies to:</p>
                <ul className="list-disc pl-6 mb-6 space-y-1">
                  <li>Enable essential website functionality</li>
                  <li>Analyze website usage and performance</li>
                  <li>Improve content and user experience</li>
                </ul>
                <p className="mb-6">You can manage cookies through your browser settings. Disabling cookies may affect certain site features.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">5. Analytics, Advertising & Third-Party Tools</h3>
                <p className="mb-4">We may use third-party tools to support analytics and site optimization, including:</p>
                <ul className="list-disc pl-6 mb-6 space-y-1">
                  <li><strong>Google Analytics</strong> – to analyze website traffic and usage patterns</li>
                  <li><strong>Google Tag Manager</strong> – to manage site tags and scripts</li>
                  <li><strong>Microsoft Clarity</strong> – to understand user interactions via behavioral analytics</li>
                </ul>
                <p className="mb-4">These tools may collect usage data through cookies and similar technologies. Data is used in aggregate form and does not include SMS consent or mobile opt-in information.</p>
                <p className="mb-6">You can manage ad personalization and analytics preferences through the respective provider settings.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">6. How We Share Your Information</h3>
                <p className="mb-4">We <strong>do not sell your personal information</strong>.</p>
                <p className="mb-4">We may share information only in the following limited circumstances:</p>
                
                <h4 className="text-lg font-semibold mb-2">Service Providers</h4>
                <p className="mb-4">With trusted vendors that assist us in operating our website and services (e.g., SMS delivery, email services, scheduling tools, analytics providers). These providers may use information <strong>only to perform services on our behalf</strong>.</p>

                <h4 className="text-lg font-semibold mb-2">Legal & Safety</h4>
                <p className="mb-4">When required by law or to protect the rights, safety, and security of Woods Spine & Wellness, our users, or others.</p>

                <h4 className="text-lg font-semibold mb-2">Business Transfers</h4>
                <p className="mb-4">If Woods Spine & Wellness is involved in a merger, acquisition, or asset sale, information may be transferred as part of that transaction. Any successor will be required to honor this Privacy Policy.</p>

                <p className="mb-6"><strong>Mobile information, SMS opt-in data, and consent records are never shared with third parties for marketing or promotional purposes.</strong></p>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">7. SMS and Mobile Messaging Privacy</h3>
                <p className="mb-4">We respect your privacy and are committed to protecting your mobile information.</p>
                <p className="mb-4"><strong>Mobile phone numbers, SMS opt-in data, and text message consent records are not sold, rented, or shared with third parties or affiliates for marketing or promotional purposes.</strong></p>
                <p className="mb-4">Text message consent is used solely for the purpose of delivering communications from us, doing business as <strong>Woods Spine & Wellness</strong>, including appointment reminders, service notifications, and messages you have explicitly agreed to receive.</p>
                <p className="mb-4">We may share limited information with trusted service providers solely to support message delivery (such as SMS platform providers), and only as necessary to operate our messaging services. <strong>All such providers are contractually prohibited from using mobile information for their own marketing or promotional purposes.</strong></p>
                <p className="mb-6">All the above categories exclude text messaging originator opt-in data and consent; this information won't be shared with any third parties.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">8. Your Privacy Choices</h3>
                <ul className="list-disc pl-6 mb-6 space-y-1">
                  <li><strong>SMS:</strong> Reply STOP to opt out</li>
                  <li><strong>Email:</strong> Use the unsubscribe link</li>
                  <li><strong>Cookies:</strong> Adjust browser settings</li>
                  <li><strong>Access & Deletion:</strong> You may request access to, correction of, or deletion of your information, subject to applicable law</li>
                </ul>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">9. Data Security</h3>
                <p className="mb-6">We implement reasonable administrative, technical, and physical safeguards to protect your information. However, no system is completely secure, and we cannot guarantee absolute security.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">10. Children’s Privacy</h3>
                <p className="mb-6">Our services are not intended for children under the age of 13, and we do not knowingly collect personal information from children.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">11. Updates to This Policy</h3>
                <p className="mb-6">We may update this Privacy Policy periodically. Any changes will be posted on this page with an updated "Last Updated" date.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-2xl font-bold text-primary mb-4">12. Contact Us</h3>
                <p className="mb-4">If you have questions about this Privacy Policy or how your information is handled, contact us at:</p>
                <div className="bg-muted p-6 rounded-lg">
                  <p className="font-bold mb-2">Woods Spine & Wellness</p>
                  <p className="mb-1"><strong>Address:</strong> 530 E LOOP 281 STE 103, Longview, TX 75605</p>
                  <p className="mb-1"><strong>Phone:</strong> (903) 215-8828</p>
                  <p><strong>Email:</strong> woodsspine.office@gmail.com</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}